import { useCallback, useMemo } from 'react';

import { useDispatch } from 'react-redux';
import { FormStructure } from '@europrocurement/flexy-form';
import { HeaderSwitch } from '@europrocurement/flexy-components';
import {
    DOSSIER_AUTOCOMPLETE_DATASOURCE_NAME,
    DOSSIERS_FORMALITES_AUTOCOMPLETE_DATASOURCE_NAME,
    DOSSIERS_FORMALITES_SLICE_NAME,
    DOSSIERS_SLICE_NAME,
    FOURNISSEUR_AUTOCOMPLETE_FACTURE_DATASOURCE_NAME,
    FOURNISSEUR_SLICE_NAME,
} from '@europrocurement/l2d-domain';
import { SectionHeadControlProps, UseHeaderSectionStructureProps } from '../types';
import { defaultProduits, replaceProduits } from '../../functions/produitFunctions';
import { resetFournisseursFields } from '../../functions/fournisseurFunctions';
import useHandleStatement from '../utils/useHandleStatement';
import { headerStructure } from '../fragments/headerSection';
import { ACTIONS } from '../../../../../../redux/FactureFormReducer';
import { AppDispatch } from '../../../../../../redux/types';
import useDomain from '../../../../../../hooks/useDomain';

const useHeaderSectionStructure = ({
    formContext,
    handleUpdateAvoir,
    mode = { type: 'register', nature: 'simple' },
    mediaObject,
    achatsFromStatementDataSource,
    isInterne,
    facture,
    headerSwitches,
    headerChildren,
}: UseHeaderSectionStructureProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { setDomainForced, resetDomainForced } = useDomain();

    const {
        addStatementButton,
        accessStatementButton,
        fetchInvoicesFromStatement,
        stateHandleStatement,
    } = useHandleStatement({
        mode,
        facture,
        mediaObject,
        achatsFromStatementDataSource,
    });

    const handleSwitchFormalityPublication = useCallback(
        (switchValue: boolean) => {
            // All dispatches below are synchronous — React 18 batches them into ONE render,
            // avoiding the asyncDispatch accumulation that caused ~0.5s delay per click with RESET.
            resetFournisseursFields(formContext);
            if (switchValue) {
                setDomainForced(3);
            } else {
                resetDomainForced();
            }
            dispatch({ type: ACTIONS.RESET_STATE });
            // Clear dossier autocomplete selections synchronously (was done via asyncDispatch in RESET)
            dispatch({
                type: `${DOSSIERS_FORMALITES_SLICE_NAME}/set${DOSSIERS_FORMALITES_AUTOCOMPLETE_DATASOURCE_NAME}Search`,
                payload: { search: '' },
            });
            dispatch({
                type: `${DOSSIERS_FORMALITES_SLICE_NAME}/delete${DOSSIERS_FORMALITES_AUTOCOMPLETE_DATASOURCE_NAME}Selected`,
            });
            dispatch({
                type: `${DOSSIERS_FORMALITES_SLICE_NAME}/setmainSearch`,
                payload: { search: '' },
            });
            dispatch({ type: `${DOSSIERS_FORMALITES_SLICE_NAME}/deletemainSelected` });
            dispatch({
                type: `${DOSSIERS_SLICE_NAME}/set${DOSSIER_AUTOCOMPLETE_DATASOURCE_NAME}Search`,
                payload: { search: '' },
            });
            dispatch({
                type: `${DOSSIERS_SLICE_NAME}/delete${DOSSIER_AUTOCOMPLETE_DATASOURCE_NAME}Selected`,
            });
            dispatch({ type: `${DOSSIERS_SLICE_NAME}/setmainSearch`, payload: { search: '' } });
            dispatch({ type: `${DOSSIERS_SLICE_NAME}/deletemainSelected` });
            dispatch({
                type: `${FOURNISSEUR_SLICE_NAME}/set${FOURNISSEUR_AUTOCOMPLETE_FACTURE_DATASOURCE_NAME}Search`,
                payload: { search: '' },
            });
            dispatch({
                type: `${FOURNISSEUR_SLICE_NAME}/delete${FOURNISSEUR_AUTOCOMPLETE_FACTURE_DATASOURCE_NAME}Selected`,
            });
            dispatch({ type: `${FOURNISSEUR_SLICE_NAME}/setmainSearch`, payload: { search: '' } });
            dispatch({ type: `${FOURNISSEUR_SLICE_NAME}/deletemainSelected` });
            replaceProduits(
                formContext,
                switchValue ? defaultProduits(3, false) : defaultProduits(1, false),
            );
            fetchInvoicesFromStatement();
        },
        [dispatch, formContext, fetchInvoicesFromStatement, resetDomainForced, setDomainForced],
    );

    const overwriteHeaderSwitches = useMemo<Array<HeaderSwitch>>(() => {
        let temporaryHeaderSwitches: Array<HeaderSwitch> = [];

        if (headerSwitches) {
            temporaryHeaderSwitches = [...temporaryHeaderSwitches, ...headerSwitches];
        }

        return temporaryHeaderSwitches;
    }, [headerSwitches]);

    const overwriteHeaderChildren = useMemo<Array<SectionHeadControlProps>>(() => {
        let temporaryHeaderChildren: Array<SectionHeadControlProps> = [];

        if (Array.isArray(headerChildren)) {
            temporaryHeaderChildren = [...temporaryHeaderChildren, ...headerChildren];
        } else {
            temporaryHeaderChildren.push(headerChildren);
        }

        temporaryHeaderChildren.push(addStatementButton);
        temporaryHeaderChildren.push(accessStatementButton);

        return temporaryHeaderChildren;
    }, [accessStatementButton, addStatementButton, headerChildren]);

    const headerSectionStructure = useMemo<Array<FormStructure>>(
        () =>
            headerStructure({
                onUpdateAvoir: handleUpdateAvoir,
                onUpdateDomaine:
                    mode.type === 'register' && mode.nature === 'simple'
                        ? handleSwitchFormalityPublication
                        : undefined,
                mode,
                headerSwitches: overwriteHeaderSwitches,
                headerChildren: overwriteHeaderChildren,
                isInterne,
            }),
        [
            handleUpdateAvoir,
            mode,
            handleSwitchFormalityPublication,
            overwriteHeaderSwitches,
            overwriteHeaderChildren,
            isInterne,
        ],
    );

    return {
        headerSectionStructure,
        stateHeaderSectionStructure: stateHandleStatement,
    };
};

export default useHeaderSectionStructure;
